// ReportDocument — THE formal WSH Risk Management (RM) Audit Report. Renders the firm's actual Rev M
// report format (cover, contents, and the five real sections in order), used by the office PackageViewer,
// the public acknowledgement page, and the printed/PDF output. Wording/structure follow
// "RM Audit Report Format (Rev M)". Evidence is referenced by annex number — files are a separate pack.
import React from 'react'

const FIRM_DEFAULT = 'CCIS Singapore Pte Ltd'
const FOOTER = 'CCIS Singapore Pte Ltd  |  (65) 6376 0550  |  query@ccis.com.sg'
const TITLE = 'WSH RISK MANAGEMENT (RM) AUDIT REPORT'

const CRITERIA = [
  'Workplace Safety and Health (Risk Management) Regulations',
  'Code of Practice on Workplace Safety and Health (WSH) Risk Management',
]
const DISCLAIMER = [
  'Auditing is based on a sampling process of the available information.',
  'Ownership of the audit report shall be maintained by the Auditing Organisation.',
  'This audit report is valid at the time and place of the audit based on the documents and evidence as presented or sampled only — and does not refer to any other matters.',
  'The audited company shall exercise their duty to ensure compliance to all the legal and other requirements that it subscribes to under the Workplace Safety and Health Acts and its subsidiary legislations.',
  'Our company and/or WSH auditors shall not be held liable for any error and/or omissions in discharge of their duties and responsibilities as independent and external WSH auditors.',
  'The client shall agree i. not to use or permit the use of our audit report or testimony or the AO’s mark in communication media such as the Internet, brochures or advertising, or other documents; ii. not to make or permit any misleading statement regarding the audit; and iii. not to use or permit the use of an audit report or testimony or any part thereof in a misleading manner.',
]
// Exact Rev M "Audit Observations Summary" question wording.
const Q = {
  combination: 'Is this a single / combined / joint / integrated audit?',
  deviation: 'Is there any deviation from the audit plan?',
  change: 'Is there any significant change that affect the management system of the client since the last audit?',
  prevNc: "Is the previous non-conformities' correction action effective?",
  comments: "Auditor's comments on the organisation / company's RM implementation (1) Strengths and (2) Areas for improvement",
  findings: 'Any findings or unresolved issues for the organisation / company to follow up?',
  recommend: 'Do you recommend the organisation / company to get bizSAFE recognition?',
}
const SECTIONS = [
  'Auditee Company Details',
  'Audit Observations Summary',
  'Information about the RM audit',
  'Risk Control Measures Implementation',
  'Audit Checklist & Other Considerations, Interview Personnel Sheet & Audit Evidences',
]

const APP_TYPE = { 'New Application': 'New', 'New': 'New', 'Renewal': 'Renewal', 'Progression': 'Progression' }

const fmtDT = v => v ? new Date(v).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' }) : '—'
const fmtD  = v => { try { return v ? new Date(v).toLocaleDateString('en-SG', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—' } catch { return v || '—' } }
const fmtT  = v => { try { return v ? new Date(v).toLocaleTimeString('en-SG', { timeZone: 'Asia/Singapore', hour: '2-digit', minute: '2-digit' }) : '—' } catch { return '—' } }
const yn    = v => v === true ? 'Yes' : v === false ? 'No' : (v || '—')
const has   = v => v != null && String(v).trim() !== ''
const plus3y = v => { try { const d = new Date(v); d.setFullYear(d.getFullYear() + 3); return fmtD(d) } catch { return '—' } }

function H({ n, children }) {
  return <h2 style={{ fontSize: 13.5, fontWeight: 700, margin: '0 0 12px', padding: '6px 10px', background: '#2b2b2b', color: '#fff' }}>{n ? `${n}. ` : ''}{children}</h2>
}
function Sub({ children }) {
  return <div style={{ fontSize: 12.5, fontWeight: 700, margin: '14px 0 8px', color: '#1a1a1a', borderBottom: '1px solid #999', paddingBottom: 3, breakAfter: 'avoid', pageBreakAfter: 'avoid' }}>{children}</div>
}
const sec = { marginBottom: 24, breakInside: 'avoid' }
const thBase = { padding: '6px 8px', border: '0.5px solid #b9b9b9', background: '#ececec', textAlign: 'left', fontWeight: 600, verticalAlign: 'top' }
const tdBase = { padding: '6px 8px', border: '0.5px solid #b9b9b9', verticalAlign: 'top' }
// label/value rows rendered as a bordered table (Rev M style), not loose app rows
function KV({ label, value, pre, hint }) {
  return (
    <tr>
      <td style={{ ...tdBase, width: 280, background: '#f6f6f4', fontWeight: 500 }}>
        {label}{hint && <div style={{ fontSize: 10.5, color: '#888', fontWeight: 400 }}>{hint}</div>}
      </td>
      <td style={{ ...tdBase, whiteSpace: pre ? 'pre-wrap' : 'normal' }}>{has(value) ? value : '—'}</td>
    </tr>
  )
}
const kvTable = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }

export default function ReportDocument({ doc, firmName = FIRM_DEFAULT, logos = {}, media = {}, templateItems = null, checklistRevision = null, onDownload, part = null }) {
  // part='front' → sections 1–4 only (no §5, no acknowledgement); part='tail' → acknowledgement only.
  // part=null → the classic full document. §5 in WSHC format is a separate component (WshcChecklistSection).
  const { client = {}, job = {}, snapshot = {}, ack = null, currentVersion } = doc || {}
  const s = snapshot?.snapshot || snapshot || {}
  // Bound checklist version — frozen in the snapshot at submit; for older snapshots the renderer resolves it
  // from checklist_template_id and passes it in. Shown beside the checklist title for SAC traceability.
  const checklistRev = s.checklist_revision || checklistRevision || (doc && doc.checklistRevision) || null
  const version = snapshot?.version ?? currentVersion ?? null
  const submittedAt = snapshot?.submitted_at || snapshot?.created_at

  const isManual = (s.execution_mode || 'system') === 'manual'
  const fm = s.frontmatter || {}
  const conduct = s.conduct || {}
  const participants = s.participants || []
  // Checklist: prefer the resolved array frozen in the snapshot. Fallback (older snapshots that predate
  // checklist capture): reconstruct from the snapshot responses joined to live template items (office side).
  let checklist = s.checklist || []
  if (!checklist.length && templateItems && (s.responses || []).length) {
    const respByItem = {}; (s.responses || []).forEach(r => { respByItem[r.template_item_id] = r })
    checklist = templateItems.map(it => ({ clause_ref: it.clause_ref, section: it.section || '', section_order: it.section_order ?? 0,
      item_text: it.item_text || '', result: respByItem[it.id]?.result || null, remarks: respByItem[it.id]?.remarks || null, annex_ref: respByItem[it.id]?.annex_ref || null }))
  }
  const prompts = s.prompts || []
  const interviews = s.interviews || []
  const rcm = s.rcm || []
  // De-dupe accidental repeat uploads — the double-upload bug attached the SAME file twice to the same slot.
  // Collapse to the first of each (evidence_type + file_name); genuinely different files (different names) stay.
  // This corrects the on-screen render; already-issued stored packages are only affected if re-generated.
  const evidence = (() => {
    const seen = new Set(), out = []
    for (const e of (s.evidence || [])) {
      const k = `${e.evidence_type || ''}|${e.file_name || ''}`
      if (seen.has(k)) continue
      seen.add(k); out.push(e)
    }
    return out
  })()
  const findings = s.findings || []
  // §1 data with fallbacks (snapshot first, then live job — so the office view fills even on old snapshots).
  const manpower = s.manpower ?? job.planning_num_employees ?? job.form_data?.num_employees ?? job.manpower ?? null
  // Multi-day audit period. Snapshot values win (self-contained); fall back to live job for old snapshots.
  // Single/half-day (no day 2) → single date + Day-1-based expiry, i.e. unchanged from before.
  const auditDate1 = s.audit_date ?? job.audit_date
  const auditDate2 = s.audit_date_day2 ?? job.audit_date_day2
  // CONSECUTIVE (Day 2 = Day 1 + 1 calendar day) → a "to" range is accurate. Otherwise the two days are
  // separate ("and"), so we never imply a continuous period for e.g. 1 Jul and 10 Jul.
  const dayGap = auditDate2 ? Math.round((new Date(auditDate2) - new Date(auditDate1)) / 86400000) : null
  const auditDatesDisplay = !auditDate2 ? fmtD(auditDate1)
    : dayGap === 1 ? `${fmtD(auditDate1)} to ${fmtD(auditDate2)}`
    : `${fmtD(auditDate1)} and ${fmtD(auditDate2)}`
  const expiryBasis = auditDate2 || auditDate1   // 3-year validity runs from the LAST audit day (display only)
  // Opening/closing MEETING date+time (from the frozen conduct record). For a multi-day audit we show the
  // date too (so it is clear which day); single/half-day shows time only, as before. '—' when not recorded.
  const meetingDisplay = (v) => !v ? '—' : (auditDate2 ? `${fmtD(v)}, ${fmtT(v)}` : fmtT(v))
  // §1 per-company blocks. A COMBINED audit freezes a companies[] array into the snapshot; a normal
  // single-company job renders a 1-element list from the client → identical output to before.
  const companies = (s.companies && s.companies.length) ? s.companies : [{
    uen: client.uen, name: client.name, manpower,
    scope_of_business: scopeOfBusiness, audit_scope: auditScopeFull, address: client.address,
  }]
  const isCombined = companies.length > 1
  // Render a per-company field: numbered "(1) … (2) …" list when combined, else the single value (as today).
  const perCo = (fn) => isCombined
    ? <>{companies.map((c, i) => <div key={i}>({i + 1}) {fn(c) ?? '—'}</div>)}</>
    : (fn(companies[0]) ?? '—')
  const appTypeRaw = s.application_type ?? job.form_data?.application_type ?? job.application_type
  const auditType = APP_TYPE[appTypeRaw] || appTypeRaw || '—'
  // "Scope of business (audit scope)" = the office-FINAL business nature from the approved quotation, frozen
  // into the snapshot at submit (client input may have been amended by the office). Fallbacks cover reports
  // submitted before this was captured. "Audit Scope (Note…)" = that business nature + the auditor's audited
  // processes/units in brackets (fm.audit_scope_additional, which the auditor cannot leave empty on submit).
  // Snapshot (office-final, from quotation) wins; for manually-created jobs with no quote, fall back to the
  // Plan & Prepare "Audit scope" field, then the job/client business nature. Quotation jobs are unaffected.
  const scopeOfBusiness = (s.scope_of_business || job.form_data?.business_nature || job.business_nature || job.audit_scope_text || client.notes || '').toString().trim()
  const auditScopeDetail = (fm.audit_scope_additional || '').toString().trim()
  const auditScopeFull = scopeOfBusiness + (auditScopeDetail ? ` (${auditScopeDetail})` : '')
  // "Audit Scope (Note…)" = the base scope + the auditor's ACTUAL processes/units audited, in brackets. Applied
  // at RENDER so it works for every snapshot: the per-company base is frozen in s.companies (base only) while the
  // auditor's text is frozen separately in s.frontmatter — combine them here. The include-guard prevents a double
  // append on the legacy single-company fallback (whose base already carries the detail).
  const withScopeDetail = (base) => {
    const b = (base || '').toString().trim()
    if (!auditScopeDetail || b.includes(auditScopeDetail)) return b
    return `${b} (${auditScopeDetail})`
  }

  // Annex references = the REAL references the auditor recorded (annex_ref). Shown only where there is an
  // actual reference, and only for "Yes" rows (N/A / No never auto-show an annex).
  const rcmPhotos = (r) => evidence.filter(e => e.storage_path && e.evidence_type === `rcm:${r.id}`)
  // Launch model: ONE supporting evidence-pack PDF, referenced by the checklist / interview annex numbers.
  // Each slot may hold MULTIPLE PDFs — keep the full list (all are appended/linked) plus the first for prose.
  const packFiles = [...evidence.filter(e => e.evidence_type === 'evidence_pack'), ...evidence.filter(e => e.evidence_type === 'audit_working_document')]
  const rcmDocFiles = evidence.filter(e => e.evidence_type === 'rcm_document')   // compiled implementation document (Section 4 doc mode)
  const checklistFiles = evidence.filter(e => e.evidence_type === 'manual_checklist')   // manual/Excel checklist (Section 5, fail-safe mode)
  const interviewFiles = evidence.filter(e => e.evidence_type === 'manual_interview')    // manual/Excel interview personnel sheet (Section 5)
  const rcmDocFile = rcmDocFiles[0]   // first implementation doc — used for the Section-4 prose
  // Render one "View →" link per uploaded file in a slot (so multiple PDFs are all reachable on screen).
  const viewLinks = (files, label) => files.filter(f => media[f.storage_path]).map((f, i) => (
    <div key={f.storage_path} style={{ marginTop: 6 }}><a href={media[f.storage_path]} target="_blank" rel="noreferrer" style={{ color: '#1a5fa8', fontWeight: 600 }}>📄 View {label}{files.length > 1 ? ` ${i + 1}` : ''} →</a></div>
  ))

  const openAtt = participants.filter(p => (p.meeting || 'opening') !== 'closing')
  const closeAtt = participants.filter(p => p.meeting === 'closing')
  const ackStale = ack && currentVersion != null && ack.report_version !== currentVersion
  // Reviewer = the actual decision-maker / reviewer who approved the report (real user data, never hardcoded).
  const reviewer = job.reviewed_by || job.approved_by || job.office_review_by || '—'
  const auditors = [job.auditor_name, job.auditor2_name].filter(Boolean).join(', ')
  const company = client.name || '[Company]'
  // COMBINED audit: every audited company must be named on the COVER and in the §3 audit objectives — not just
  // the primary one (§1 already lists them all via perCo). Falls back to the single client for a normal audit.
  const companyNames = companies.map(c => (c?.name || '').trim()).filter(Boolean)
  const coverNames = (isCombined && companyNames.length) ? companyNames : [company]
  const companySentence = (isCombined && companyNames.length > 1)
    ? `${companyNames.slice(0, -1).join(', ')} and ${companyNames[companyNames.length - 1]}`
    : (companyNames[0] || company)
  // Report reference: a structured report_reference_no when set; quote_ref is the temporary fallback.
  const refNo = job.report_reference_no || job.quote_ref || '—'
  // Management Representative + Signature come ONLY from the client's electronic acknowledgement — they stay
  // blank while the report is in office review / out for sign-off, and fill in when the client signs.
  const acked = ack && !ackStale
  const mgmtRep = acked ? ([ack.ack_name, ack.ack_designation].filter(Boolean).join(', ') || '—') : '—'
  const ackSignature = acked
    ? (
      <>
        <div>{[ack.ack_name, ack.ack_designation].filter(Boolean).join(', ')}</div>
        <div style={{ color: '#777', fontSize: 12, marginTop: 2 }}>(Electronically signed on {new Date(ack.submitted_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}{ack.ip_address ? ` · IP ${ack.ip_address}` : ''})</div>
      </>
    )
    : '— (pending client acknowledgement)'

  // Full transparency: ALL checklist questions are shown (Yes / No / N/A), never hidden.
  // grouped by section (real section names from the seed)
  const groups = []
  for (const it of checklist) {
    let g = groups.find(x => x.section === (it.section || ''))
    if (!g) { g = { section: it.section || '', order: it.section_order ?? 0, items: [] }; groups.push(g) }
    g.items.push(it)
  }
  groups.sort((a, b) => a.order - b.order)
  const questionFor = c => { const qs = prompts.filter(p => p.clause_ref === c && has(p.question)).map(p => p.question); return qs.length ? qs.join('  •  ') : null }
  // Interview sheet ordered by checklist sequence (AH/clause order), not DB insertion order.
  const clauseOrder = {}; checklist.forEach((it, i) => { if (it.clause_ref != null && clauseOrder[it.clause_ref] == null) clauseOrder[it.clause_ref] = i })
  const clauseRank = c => (clauseOrder[c] != null ? clauseOrder[c] : 9999)
  const interviewsSorted = interviews.slice().sort((a, b) => clauseRank(a.clause_ref) - clauseRank(b.clause_ref) || String(a.clause_ref || '').localeCompare(String(b.clause_ref || ''), undefined, { numeric: true }))

  return (
    <div className="rpt-doc" style={{ fontSize: 12.5, color: '#1a1a1a', lineHeight: 1.5, fontFamily: "'CCISReport', Arial, Helvetica, sans-serif" }}>
      {/* Self-hosted report font (deterministic on any renderer, incl. headless Chromium — no Google/system
          dependency) + print pagination rules: never split a table row or an image, repeat table headers on
          every page a table spans, and keep section headings with the content that follows them. */}
      <style>{`
        @font-face { font-family:'CCISReport'; font-weight:400; font-style:normal; src:url('/fonts/report-sans.ttf') format('truetype'); font-display:swap; }
        @font-face { font-family:'CCISReport'; font-weight:700; font-style:normal; src:url('/fonts/report-sans-bold.ttf') format('truetype'); font-display:swap; }
        .rpt-doc table { break-inside: auto; }
        .rpt-doc tr { break-inside: avoid; page-break-inside: avoid; }
        .rpt-doc thead { display: table-header-group; }
        .rpt-doc img { break-inside: avoid; page-break-inside: avoid; }
        .rpt-doc h2 { break-after: avoid; page-break-after: avoid; }
      `}</style>
      {part !== 'tail' && <>
      {/* ── COVER ── same look as the Internal Audit report cover (IAReportDocument): CCIS + SAC logos side by side top
            left, teal left-aligned title block. Only the inside is styled — the outer box (own page, break after) is
            unchanged so pagination stays exactly as before. The box fills the printed page (A4 297mm less the
            renderer's 14mm top + bottom margins = 269mm, kept a few mm short so it can never spill onto a second page)
            so the footer line sits at the bottom of the page. */}
      <div style={{ minHeight: '262mm', display: 'flex', flexDirection: 'column', breakAfter: 'page', pageBreakAfter: 'always' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {logos.company ? <img src={logos.company} alt="Company logo" style={{ maxHeight: 56, maxWidth: 150, objectFit: 'contain' }} /> : null}
          {logos.sac ? <img src={logos.sac} alt="SAC logo" style={{ maxHeight: 110, maxWidth: 230, objectFit: 'contain' }} /> : null}
        </div>
        <div style={{ flex: 1, marginTop: '45mm' }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#0e5c5c', maxWidth: 560, lineHeight: 1.25, letterSpacing: '0.01em' }}>{TITLE}</div>
          <div style={{ height: 4, width: 120, background: '#0e5c5c', margin: '18px 0' }} />
          <div style={{ fontSize: 20, fontWeight: 600, color: '#1d2b2b', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {coverNames.map((n, i) => <div key={i}>{n}</div>)}
          </div>
          <div style={{ fontSize: 13, color: '#5c6b6a', marginTop: 18, lineHeight: 1.8 }}>
            <div>Issued On: {fmtD(submittedAt)}</div>
            <div>Ref No.: {refNo}</div>
            {version != null && <div>Report Version: {version}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: 10.5, color: '#999', borderTop: '1px solid #d6e0df', paddingTop: 10, marginTop: 24, whiteSpace: 'nowrap' }}>
          <strong style={{ color: '#666' }}>{FOOTER.split('|')[0].trim()}</strong>{'  |  ' + FOOTER.split('|').slice(1).map(x => x.trim()).join('  |  ')}
        </div>
      </div>

      {/* ── TABLE OF CONTENTS ── */}
      <div style={{ ...sec, breakAfter: 'page', pageBreakAfter: 'always' }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 14 }}>Table of Contents</h2>
        {SECTIONS.map((t, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'baseline', padding: '7px 0', fontSize: 13, borderBottom: '0.5px dotted #cfcfcf' }}>
            <span style={{ fontWeight: 500 }}>{i + 1}.&nbsp;</span><span>{t}</span>
            <span style={{ flex: 1, borderBottom: '1px dotted #bbb', margin: '0 8px', transform: 'translateY(-3px)' }} />
          </div>
        ))}
      </div>

      {/* ── 1. AUDITEE COMPANY DETAILS ── */}
      <div style={sec}>
        <H n={1}>Auditee Company Details</H>
        <table style={kvTable}><tbody>
          <KV label="Unique Entity Number (UEN)" hint="Refer to www.uen.gov.sg" value={perCo(c => c.uen)} pre />
          <KV label="Manpower size declared" value={perCo(c => c.manpower)} pre />
          {/* On-site verified manpower is stored separately from the declared/planning figure. Surface it on the
              report whenever the auditor's confirmed number differs, so the report reflects what was verified
              on-site (not just what was declared at planning). Display only — CT17 control logic is unchanged. */}
          {isCombined
            ? <KV label="Manpower size (verified on-site)" value={perCo(c => c.verified_manpower)} pre />
            : (s.verified_manpower != null && String(s.verified_manpower) !== String(manpower ?? '') && (
                <KV label="Manpower size (verified on-site)" value={s.verified_manpower} pre />
              ))}
          <KV label="Company name as per UEN" value={perCo(c => c.name)} pre />
          <KV label="Company Address" value={companies[0].address} pre />
          <KV label="Scope of business (audit scope)" value={perCo(c => c.scope_of_business)} pre />
          <KV label={auditDate2 ? 'Audit date(s)' : 'Audit date'} value={auditDatesDisplay} />
          <KV label="Opening meeting" hint="Opening meeting date/time" value={meetingDisplay(conduct.opening_at)} />
          <KV label="Closing meeting" hint="Closing meeting date/time" value={meetingDisplay(conduct.closing_at)} />
          <KV label="Audit report expiry date" hint="Valid for 3 years from the date of audit" value={expiryBasis ? plus3y(expiryBasis) : '—'} />
          <KV label="Audit Type (New / Progression / Renewal)" value={perCo(c => c.application_type ? (APP_TYPE[c.application_type] || c.application_type) : auditType)} />
          <KV label="Audit Scope (Note: Provide the details of organisational / functional units / processes audited)" value={perCo(c => withScopeDetail(c.audit_scope))} pre />
          <KV label="Location of audit" value={fm.audit_location_actual || job.audit_site_address} pre />
          {/* Project name — shown ONLY when the field is genuinely in play, so PAST reports are untouched:
               • the auditor amended it — project_name_actual present; '' means "cleared", still in play → "—", OR
               • a planned project name actually has a value.
              A report where it was never keyed (no amendment, blank/null planned) shows NO row — identical to
              how every previous report renders, including ones that carry project_name: null. */}
          {(() => {
            const amended = fm.project_name_actual                       // '' = cleared (in play); null/undefined = not amended / reverted
            const isAmended = amended !== undefined && amended !== null
            const pn = isAmended ? amended : s.project_name
            if (!isAmended && !has(s.project_name)) return null          // never keyed → hidden, exactly as before
            return <KV label="Project name" value={has(pn) ? pn : '—'} pre />
          })()}
          <KV label="Management Representative's name and designation" hint="From the client's acknowledgement below" value={mgmtRep} />
          <KV label="Signature" hint="Electronic client sign-off (see Client Acknowledgement below)" value={ackSignature} />
        </tbody></table>
      </div>

      {/* ── 2. AUDIT OBSERVATIONS SUMMARY ── */}
      <div style={{ ...sec, breakBefore: 'page', pageBreakBefore: 'always' }}>
        <H n={2}>Audit Observations Summary</H>
        <div style={{ fontSize: 11.5, fontStyle: 'italic', color: '#791F1F', marginBottom: 10 }}>Note: Do NOT submit the bizSAFE application until all findings are closed.</div>
        <table style={kvTable}><tbody>
          {/* ONE "Justifications (if any)" field, per the Rev M template: office planning justifications FIRST
              (approach notes, then duration justification — both frozen in the snapshot at submit, so the auditor
              cannot delete them and every pre-change snapshot renders exactly as before), then the auditor's own
              additions behind. Guard: an in-flight audit may have SAVED the old pre-fill (the office duration
              justification) into the auditor field — dropped here so the same text never prints twice. */}
          {(() => {
            const aud = String(fm.justifications_additional || '').trim()
            // Drop an office paragraph when the auditor's text already CONTAINS it (covers both the saved old
            // pre-fill verbatim AND pre-fill-plus-their-own-additions) — the same words must never print twice.
            const office = [s.audit_approach_note, s.manday_justification].filter(has).map(v => String(v).trim())
              .filter(o => !(aud && aud.includes(o)))
            return <KV label="Justifications (if any)" value={[...office, aud || null].filter(Boolean).join('\n\n')} pre />
          })()}
          <KV label={Q.combination} value={fm.audit_combination} />
          <KV label={Q.deviation} value={fm.deviation_from_plan === true ? `Yes — ${fm.deviation_reason || ''}` : yn(fm.deviation_from_plan)} pre />
          <KV label={Q.change} value={fm.significant_change === true ? `Yes — ${fm.significant_change_reason || ''}` : yn(fm.significant_change)} pre />
          <KV label={Q.prevNc} value={has(fm.prev_nc_reason) ? `No — ${fm.prev_nc_reason}` : '—'} pre />
          <KV label={Q.comments} value={`Strengths:\n${fm.strengths || '—'}\n\nAreas for improvement:\n${fm.areas_for_improvement || '—'}`} pre />
          <KV label={Q.findings} value={fm.unresolved_findings_answer === 'Yes' ? `Yes — ${fm.unresolved_findings_summary || ''}` : (fm.unresolved_findings_answer || '—')} pre />
          <KV label={Q.recommend} value={`${yn(fm.recommend_bizsafe)}${has(fm.recommend_reason) ? ' — ' + fm.recommend_reason : ''}`} pre />
          <KV label="Findings raised against checklist clauses" value={findings.length ? findings.join(', ') : 'None'} />
        </tbody></table>

        <Sub>This audit report is issued by</Sub>
        <table style={kvTable}><tbody>
          <KV label="Auditor's name and signature" value={
            (s.signatures && s.signatures.length) ? (
              // One row, each auditor's name with their own signature line below (co-auditor first, lead last).
              <>
                {s.signatures.map((sg, i) => (
                  <div key={i} style={i ? { marginTop: 12 } : undefined}>
                    <div>{sg.name}</div>
                    <div style={{ color: '#777', fontSize: 12, marginTop: 2 }}>(Electronically signed on {new Date(sg.signed_at).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}{sg.ip ? ` · IP ${sg.ip}` : ''})</div>
                  </div>
                ))}
              </>
            ) : (
              // Legacy / single-signature reports — unchanged.
              <>
                <div>{auditors || '—'}</div>
                {submittedAt && <div style={{ color: '#777', fontSize: 12, marginTop: 2 }}>(Electronically signed on {new Date(submittedAt).toLocaleString('en-SG', { timeZone: 'Asia/Singapore' })}{s.auditor_signed_ip ? ` · IP ${s.auditor_signed_ip}` : ''})</div>}
              </>
            )
          } />
          <KV label="Reviewer's name" value={reviewer} />
          <KV label="Auditing organisation" value={firmName} />
          <KV label="Auditing organisation's company stamp" value={logos.stamp ? <img src={logos.stamp} alt="Company stamp" style={{ maxHeight: 80, maxWidth: 170, objectFit: 'contain' }} /> : '—'} />
        </tbody></table>
      </div>

      {/* ── 3. INFORMATION ABOUT THE RM AUDIT (§4 RCM follows on the same page when short) ── */}
      <div style={{ ...sec, breakBefore: 'page', pageBreakBefore: 'always' }}>
        <H n={3}>Information about the RM audit</H>
        <Sub>Audit objectives</Sub>
        <p style={{ fontSize: 12.5, margin: '0 0 12px' }}>
          To verify that {companySentence} {isCombined ? 'are' : 'is'} recommended to get bizSAFE recognition by conducting risk assessments and
          implementing risk control measures according to the requirements under the WSH (Risk Management) Regulations.
        </p>
        <Sub>Audit reference standard / Criteria</Sub>
        <ul style={{ margin: '0 0 12px', paddingLeft: 20, fontSize: 12.5 }}>{CRITERIA.map((c, i) => <li key={i}>{c}</li>)}</ul>
        <Sub>Disclaimer</Sub>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 11.5, color: '#333' }}>{DISCLAIMER.map((d, i) => <li key={i} style={{ marginBottom: 4 }}>{d}</li>)}</ul>
      </div>

      {/* ── 4. RISK CONTROL MEASURES IMPLEMENTATION ── */}
      <div style={sec}>
        <H n={4}>Risk Control Measures Implementation</H>
        {isManual ? (
          rcmDocFile ? (
            <div style={{ fontSize: 12.5, color: '#444' }}>
              The risk control measure implementation photographs are provided in the appended implementation document{rcmDocFile.file_name ? <> (<strong>{rcmDocFile.file_name}</strong>{rcmDocFiles.length > 1 ? ` +${rcmDocFiles.length - 1} more` : ''})</> : ''}.
              {!onDownload && viewLinks(rcmDocFiles, 'implementation document')}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: '#444' }}>Implementation photographs were not provided for this audit. Reason: {s.conduct?.no_implementation_reason || '—'}.</div>
          )
        ) : s.rcm_mode === 'document' ? (
          <div style={{ fontSize: 12.5, color: '#444' }}>
            The risk control measure implementation observations and photographs are provided in the compiled
            implementation document{rcmDocFile ? <> (<strong>{rcmDocFile.file_name}</strong>{rcmDocFiles.length > 1 ? ` +${rcmDocFiles.length - 1} more` : ''})</> : ''}, which is appended
            after this report as the official implementation evidence.
            {!onDownload && viewLinks(rcmDocFiles, 'implementation document')}
          </div>
        ) : rcm.length === 0 ? <div style={{ fontSize: 12, color: '#777' }}>No implementation entries recorded.</div>
          : <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead><tr><th style={{ ...thBase, width: 40 }}>S/N</th><th style={{ ...thBase, width: 230 }}>Photos</th><th style={thBase}>Observations and Remarks</th></tr></thead>
              <tbody>{rcm.slice().sort((a, b) => (a.serial_no || 0) - (b.serial_no || 0)).map((r, i) => {
                const photos = rcmPhotos(r)
                return (
                  <tr key={i}>
                    <td style={{ ...tdBase, textAlign: 'center' }}>{r.serial_no ?? i + 1}</td>
                    <td style={tdBase}>
                      {photos.length === 0 ? '—' : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {photos.map((p, pi) => {
                            const url = media[p.storage_path]
                            return url ? (
                              <div key={pi} style={{ width: 104 }}>
                                <img src={url} alt={p.file_name || 'RCM photo'} style={{ width: '100%', borderRadius: 4, border: '0.5px solid #ccc', display: 'block' }} />
                                {p.file_name && <div style={{ fontSize: 9.5, color: '#888', marginTop: 2, wordBreak: 'break-all' }}>{p.file_name}</div>}
                              </div>
                            ) : (
                              <div key={pi} style={{ fontSize: 11, color: '#555', border: '0.5px solid #ddd', borderRadius: 4, padding: '4px 6px' }}>📎 {p.file_name || 'Photo'}</div>
                            )
                          })}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdBase, whiteSpace: 'pre-wrap' }}>{r.observation || '—'}</td>
                  </tr>
                )
              })}</tbody>
            </table>}
      </div>

      </>}
      {part === null && <>
      {/* ── 5. CHECKLIST + INTERVIEWS + EVIDENCES (starts on a FRESH page; may span many pages) ── */}
      <div style={{ ...sec, breakBefore: 'page', pageBreakBefore: 'always', breakInside: 'auto', pageBreakInside: 'auto' }}>
        <H n={5}>Audit Checklist &amp; Other Considerations, Interview Personnel Sheet &amp; Audit Evidences</H>

        <Sub>RM Audit Checklist{checklistRev ? ` (Revision ${checklistRev})` : ''}</Sub>
        {isManual ? (
          <div style={{ fontSize: 12.5, color: '#444' }}>
            <p style={{ margin: 0 }}>This audit was conducted using the manual / Excel fail-safe method. Reason: {s.manual_failsafe_reason || '—'}. The completed checklist, implementation evidence and supporting documents are appended after this report.</p>
            {!onDownload && viewLinks(checklistFiles, 'completed checklist')}
          </div>
        ) : checklist.length === 0 ? <div style={{ fontSize: 12, color: '#854F0B', fontStyle: 'italic' }}>Checklist detail is unavailable for this report version (submitted before checklist capture; re-submit to include the full checklist).</div>
          : groups.map((g, gi) => (
            <div key={gi} style={{ marginBottom: 12 }}>
              {has(g.section) && <div style={{ fontSize: 12, fontWeight: 700, background: '#dfe6d4', padding: '4px 8px', border: '0.5px solid #b9b9b9', borderBottom: 'none', breakAfter: 'avoid', pageBreakAfter: 'avoid' }}>{g.section}</div>}
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={{ ...thBase, width: 48 }}>Clause</th><th style={thBase}>Requirement</th>
                  <th style={{ ...thBase, width: 50 }}>Result</th><th style={{ ...thBase, width: 52 }}>Annex</th><th style={{ ...thBase, width: '28%' }}>Remarks</th>
                </tr></thead>
                <tbody>{g.items.map((it, i) => (
                  <tr key={i}>
                    <td style={{ ...tdBase, whiteSpace: 'nowrap', fontWeight: 600 }}>{it.clause_ref || '—'}</td>
                    <td style={{ ...tdBase, whiteSpace: 'pre-wrap' }}>{it.item_text || '—'}</td>
                    <td style={{ ...tdBase, fontWeight: 600, color: it.result === 'No' ? '#791F1F' : it.result === 'N/A' ? '#666' : '#27500a' }}>{it.result || '—'}</td>
                    <td style={{ ...tdBase, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{it.result === 'Yes' && has(it.annex_ref) ? it.annex_ref : ''}</td>
                    <td style={{ ...tdBase, whiteSpace: 'pre-wrap' }}>{it.remarks || ''}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ))}

        <div style={{ breakBefore: 'page', pageBreakBefore: 'always' }}>
        <Sub>Interview Personnel Sheet</Sub>
        {isManual ? (
          <div style={{ fontSize: 12.5, color: '#444' }}>
            The interview personnel sheet was completed on the manual / Excel form and is appended after this report.
            {!onDownload && viewLinks(interviewFiles, 'interview personnel sheet')}
          </div>
        ) : interviews.length === 0 ? <div style={{ fontSize: 12, color: '#777' }}>No interviews recorded.</div>
          : <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...thBase, width: 48 }}>Clause</th><th style={{ ...thBase, width: '22%' }}>Personnel (name / designation)</th>
                <th style={thBase}>Question</th><th style={thBase}>Response</th>
              </tr></thead>
              <tbody>{interviewsSorted.map((iv, i) => (
                <tr key={i}>
                  <td style={{ ...tdBase, whiteSpace: 'nowrap' }}>{iv.clause_ref || '—'}</td>
                  <td style={tdBase}>{iv.interviewee_name || '—'}{iv.designation ? <div style={{ color: '#777' }}>{iv.designation}</div> : ''}{iv.job_description ? <div style={{ color: '#999', fontSize: 10.5 }}>{iv.job_description}</div> : ''}</td>
                  <td style={{ ...tdBase, whiteSpace: 'pre-wrap' }}>{has(iv.question) ? iv.question : (questionFor(iv.clause_ref) || '—')}</td>
                  <td style={{ ...tdBase, whiteSpace: 'pre-wrap' }}>{iv.response || '—'}</td>
                </tr>
              ))}</tbody>
            </table>}
        <div style={{ fontSize: 10.5, color: '#999', marginTop: 4 }}>Interviews conducted on {fmtD(job.audit_date)} by {auditors || '—'}.</div>
        </div>

        <Sub>Audit Evidence Pack</Sub>
        <p style={{ fontSize: 12, color: '#444', margin: '0 0 6px' }}>
          The supporting audit evidence is maintained as a single annexed PDF evidence pack, referenced by the
          checklist and interview annex numbers above. The auditor marks the corresponding annex number on each
          supporting document within the pack.
        </p>
        {/* File listing + Open button removed (redundant — the pack is physically appended below). The
            explanatory sentence above stays. The missing-pack warning is kept as a data-integrity signal. */}
        {!packFiles.length && <div style={{ fontSize: 12, color: '#854F0B' }}>No evidence pack attached yet.</div>}
      </div>

      </>}

      {/* ── CLIENT ACKNOWLEDGEMENT (electronic) ── */}
      {(part === null || part === 'tail') && <section style={{ marginTop: 26, border: '2px solid #2b2b2b', padding: 18, breakInside: 'avoid', pageBreakInside: 'avoid', breakBefore: 'page', pageBreakBefore: 'always' }}>
        <div style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Client Acknowledgement</div>
        <p style={{ fontStyle: 'italic', fontSize: 13, margin: '0 0 16px' }}>I confirm that I have reviewed this audit report and acknowledge its contents.</p>
        {ack && !ackStale ? (
          <>
            <table style={kvTable}><tbody>
              <KV label="Name" value={ack.ack_name} />
              <KV label="Designation" value={ack.ack_designation} />
              <KV label="Company" value={ack.ack_company} />
              <KV label="Date & time" value={fmtDT(ack.submitted_at)} />
              <KV label="Report version" value={ack.report_version} />
              <KV label="Method" value="Electronic acknowledgement via the secure system link" />
            </tbody></table>
            <div style={{ fontSize: 9.5, color: '#aaa', marginTop: 8 }}>
              System-generated record (cannot be edited) — {ack.access_email ? `secure link issued to ${ack.access_email}; ` : ''}recorded {fmtDT(ack.submitted_at)}{ack.ip_address ? `; IP ${ack.ip_address}` : ''}.
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: ackStale ? '#854F0B' : '#777', background: ackStale ? '#FFF3E0' : '#f6f6f4', borderRadius: 4, padding: '12px 14px' }}>
            {ackStale
              ? `A previous acknowledgement (version ${ack.report_version}) is no longer valid because this report was revised to version ${currentVersion}. Re-acknowledgement of the current version is required.`
              : 'This report has not yet been acknowledged by the client.'}
          </div>
        )}
      </section>}

      {(part === null || part === 'tail') && <div style={{ textAlign: 'center', fontSize: 10, color: '#aaa', marginTop: 18, borderTop: '1px solid #ddd', paddingTop: 8 }}>{FOOTER}</div>}
    </div>
  )
}
